import * as THREE from 'three';
import type { LoopHandle } from '../audio/AudioEngine';
import { possessive, type JobOffer } from '../content/jobs';
import type { GameContext } from '../content/types';
import { absoluteMinutes } from '../state/rules';
import type { ActiveJob } from '../state/types';
import type { Player } from '../entities/Player';
import { fridge, mattress, recliner, washer } from '../world/builders/props';
import { HAUL_SITES, MOW_SITES, SPOTS } from '../world/layout';
import { box, cyl, M, mat } from '../world/materials';
import type { ParticleSystem } from '../world/Particles';

export interface Objective {
  text: string;
  target: THREE.Vector3 | null;
}

const GRASS_SPACING = 0.8;
const CUT_RADIUS = 0.75;

export function buildMower(): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.55, 0.22, 0.7, mat('#c0392b', { metal: 0.3 }), 0, 0.22, 0));
  g.add(cyl(0.12, 0.14, 0.2, M.black, 0, 0.42, -0.05, 8));
  for (const x of [-0.3, 0.3])
    for (const z of [-0.28, 0.28]) {
      const w = cyl(0.1, 0.1, 0.06, M.rubber, x, 0.1, z, 10);
      w.rotation.z = Math.PI / 2;
      g.add(w);
    }
  const handle = box(0.04, 0.9, 0.04, M.steel, -0.22, 0.65, -0.55);
  handle.rotation.x = -0.6;
  g.add(handle, box(0.04, 0.9, 0.04, M.steel, 0.22, 0.65, -0.55).rotateX(-0.6));
  g.add(box(0.5, 0.04, 0.04, M.black, 0, 1.0, -0.82));
  return g;
}

export function buildCargo(name: string): THREE.Group {
  if (name.includes('fridge')) return fridge();
  if (name.includes('mattress')) {
    const m = mattress();
    m.rotation.z = Math.PI / 2;
    return m;
  }
  if (name.includes('recliner')) return recliner('#5b4a3a');
  if (name.includes('washing')) return washer();
  const g = new THREE.Group();
  g.add(box(1.8, 0.5, 0.8, mat('#6b4a2a'), 0, 0.3, 0));
  g.add(box(1.8, 0.5, 0.2, mat('#6b4a2a'), 0, 0.7, -0.3));
  return g;
}

/** Odd jobs from the laundromat board: mowing lawns and hauling junk. */
export class Jobs {
  private grass: THREE.InstancedMesh | null = null;
  private cut: Uint8Array | null = null;
  private positions: Float32Array | null = null;
  private cutCount = 0;
  private mower: THREE.Group | null = null;
  private cargo: THREE.Group | null = null;
  private mowerSound: LoopHandle | null = null;
  private readonly m4 = new THREE.Matrix4();
  private readonly tmp = new THREE.Vector3();

  constructor(
    private readonly ctx: GameContext,
    private readonly parent: THREE.Object3D,
    private readonly player: Player,
    private readonly clippings: ParticleSystem,
    private readonly startLoop: () => LoopHandle,
  ) {}

  get job(): ActiveJob | null {
    return this.ctx.store.state.job;
  }

  accept(offer: JobOffer): void {
    const { store } = this.ctx;
    if (store.state.job) this.cancel(false);
    const job: ActiveJob = {
      id: offer.id,
      kind: offer.kind,
      title: offer.title,
      pay: offer.pay,
      deadline: absoluteMinutes(store.day, store.minutes) + offer.minutes,
      site: offer.site,
      cargo: offer.cargo,
      stage: 'start',
      progress: 0,
    };
    store.state.job = job;
    store.setFlag(`job_taken_${offer.id}`, true);
    if (job.kind === 'mow') this.setupMow(job);
    else this.setupHaul(job);
    store.toast(`New job: ${job.title}`, 'info', ':clipboard:');
  }

  /** Abandon the current job (and tidy up its props). */
  cancel(announce = true): void {
    const { store } = this.ctx;
    if (!store.state.job) return;
    if (announce) store.toast('Job abandoned. Somewhere, a boss sighs.', 'bad', ':clipboard:');
    this.teardown();
    store.state.job = null;
    store.changed();
  }

  private teardown(): void {
    this.mowerSound?.stop();
    this.mowerSound = null;
    if (this.player.pushing) this.player.pushing = null;
    this.mower?.removeFromParent();
    this.mower = null;
    if (this.cargo) {
      if (this.player.carrying === this.cargo) this.player.setCarrying(null);
      this.cargo.removeFromParent();
    }
    this.cargo = null;
    if (this.grass) {
      this.grass.removeFromParent();
      this.grass.geometry.dispose();
    }
    this.grass = null;
    this.cut = null;
    this.positions = null;
  }

  private setupMow(job: ActiveJob): void {
    const site = MOW_SITES[job.site];
    if (!site) return;
    const a = site.area;
    const xs = Math.floor((a.maxX - a.minX) / GRASS_SPACING);
    const zs = Math.floor((a.maxZ - a.minZ) / GRASS_SPACING);
    const count = xs * zs;
    const blade = new THREE.ConeGeometry(0.22, 0.75, 4);
    blade.translate(0, 0.37, 0);
    this.grass = new THREE.InstancedMesh(blade, mat('#5f8f30', { flat: true }), count);
    this.grass.receiveShadow = true;
    this.positions = new Float32Array(count * 2);
    this.cut = new Uint8Array(count);
    this.cutCount = 0;
    const q = new THREE.Quaternion();
    let i = 0;
    for (let ix = 0; ix < xs; ix++)
      for (let iz = 0; iz < zs; iz++) {
        const x = a.minX + (ix + 0.5) * GRASS_SPACING + (Math.random() - 0.5) * 0.3;
        const z = a.minZ + (iz + 0.5) * GRASS_SPACING + (Math.random() - 0.5) * 0.3;
        this.positions[i * 2] = x;
        this.positions[i * 2 + 1] = z;
        q.setFromEuler(new THREE.Euler(0, Math.random() * 6, 0));
        const s = 0.8 + Math.random() * 0.5;
        this.m4.compose(this.tmp.set(x, 0, z), q, new THREE.Vector3(s, s, s));
        this.grass.setMatrixAt(i, this.m4);
        i++;
      }
    this.parent.add(this.grass);
    this.mower = buildMower();
    this.mower.position.set(a.maxX - 0.8, 0, a.maxZ + 0.6);
    this.parent.add(this.mower);
  }

  private setupHaul(job: ActiveJob): void {
    const site = HAUL_SITES[job.site];
    if (!site) return;
    this.cargo = buildCargo(job.cargo ?? 'junk');
    this.cargo.position.set(site.x, 0, site.z);
    this.parent.add(this.cargo);
  }

  /** Where to press E for the current job step, if anything. */
  interaction(): { at: { x: number; z: number }; prompt: string; action: () => void } | null {
    const job = this.job;
    if (!job || job.stage !== 'start') return null;
    if (job.kind === 'mow' && this.mower) {
      const m = this.mower;
      return {
        at: { x: m.position.x, z: m.position.z },
        prompt: 'Start the mower (pull cord x3)',
        action: () => {
          job.stage = 'active';
          this.player.pushing = m;
          this.mowerSound = this.startLoop();
          this.ctx.sfx('clank');
        },
      };
    }
    if (job.kind === 'haul' && this.cargo) {
      const c = this.cargo;
      return {
        at: { x: c.position.x, z: c.position.z },
        prompt: `Pick up the ${job.cargo}`,
        action: () => {
          job.stage = 'deliver';
          this.player.setCarrying(c);
          c.position.set(0, 0, 0);
          c.scale.setScalar(0.85);
          this.ctx.sfx('thud');
        },
      };
    }
    return null;
  }

  objective(): Objective | null {
    const job = this.job;
    if (!job) return null;
    const { store } = this.ctx;
    const left = Math.max(0, job.deadline - absoluteMinutes(store.day, store.minutes));
    const time = `${Math.floor(left / 60)}h ${Math.floor(left % 60)}m left`;
    if (job.kind === 'mow') {
      const site = MOW_SITES[job.site];
      if (job.stage === 'start' && this.mower)
        return {
          text: `Grab the mower at ${possessive(site?.owner ?? 'the')} lot (${time})`,
          target: this.mower.position,
        };
      return { text: `Mow it! ${Math.round(job.progress * 100)}% (${time})`, target: null };
    }
    if (job.stage === 'start' && this.cargo)
      return { text: `Pick up the ${job.cargo} (${time})`, target: this.cargo.position };
    return {
      text: `Haul it to Junkyard Jerry's drop-off (${time})`,
      target: new THREE.Vector3(SPOTS.junkyardDrop.x, 0, SPOTS.junkyardDrop.z),
    };
  }

  update(): void {
    const job = this.job;
    if (!job) return;
    const { store } = this.ctx;
    const now = absoluteMinutes(store.day, store.minutes);

    if (
      job.kind === 'mow' &&
      job.stage === 'active' &&
      this.grass &&
      this.cut &&
      this.positions &&
      this.mower
    ) {
      const f = this.player.facing;
      const mx = this.player.position.x + Math.sin(f) * 0.9;
      const mz = this.player.position.z + Math.cos(f) * 0.9;
      this.mower.position.set(mx, 0, mz);
      this.mower.rotation.y = f;
      let changed = false;
      const n = this.cut.length;
      for (let i = 0; i < n; i++) {
        if (this.cut[i]) continue;
        const dx = (this.positions[i * 2] as number) - mx;
        const dz = (this.positions[i * 2 + 1] as number) - mz;
        if (dx * dx + dz * dz > CUT_RADIUS * CUT_RADIUS) continue;
        this.cut[i] = 1;
        this.cutCount++;
        this.grass.getMatrixAt(i, this.m4);
        this.m4.scale(this.tmp.set(1, 0.15, 1));
        this.grass.setMatrixAt(i, this.m4);
        changed = true;
        if (Math.random() < 0.5)
          this.clippings.spawn({
            x: mx,
            y: 0.3,
            z: mz,
            vx: (Math.random() - 0.5) * 3,
            vy: 2 + Math.random() * 2,
            vz: (Math.random() - 0.5) * 3,
            life: 0.7,
            size: 0.12,
            color: '#6fa83a',
            gravity: 9,
          });
      }
      if (changed) this.grass.instanceMatrix.needsUpdate = true;
      job.progress = this.cutCount / n;
      if (job.progress >= 0.92) {
        this.finish(Math.round(job.pay * Math.min(1, job.progress + 0.08)), 'mow');
        return;
      }
    }

    if (job.kind === 'haul' && job.stage === 'deliver') {
      const d = Math.hypot(
        this.player.position.x - SPOTS.junkyardDrop.x,
        this.player.position.z - SPOTS.junkyardDrop.z,
      );
      if (d < 3) {
        this.finish(job.pay, 'haul');
        return;
      }
    }

    if (now > job.deadline) {
      const partial = job.kind === 'mow' ? Math.round(job.pay * job.progress * 0.6) : 0;
      this.teardown();
      store.state.job = null;
      if (partial > 0) store.earn(partial);
      store.toast(
        partial > 0
          ? `Out of time. They paid you $${partial} for what you got done.`
          : 'Out of time! Job lost. Your boss "expected more."',
        'bad',
        ':alarm-clock:',
      );
      store.changed();
    }
  }

  private finish(pay: number, kind: 'mow' | 'haul'): void {
    const { store, sfx, ui } = this.ctx;
    const job = this.job as ActiveJob;
    this.teardown();
    store.state.job = null;
    store.incr('jobs');
    sfx('cash');
    const owner = kind === 'mow' ? MOW_SITES[job.site]?.owner : HAUL_SITES[job.site]?.owner;
    ui.openScene({
      title: "It Ain't Much, But It's Honest Work",
      art: kind === 'mow' ? ':sprout:' : ':box-truck:',
      text:
        kind === 'mow'
          ? `${owner} looks over the lawn, nods once, and pays you. Best review you've ever gotten.`
          : `Junkyard Jerry signs for the ${job.cargo} without looking at it. ${owner} pays up.`,
      choices: [],
      leave: `Collect $${pay}`,
    });
    store.apply({ money: pay, clout: 2 });
  }
}
